import * as React from "react";
import SpectatorCard from "src/components/shared/Cards/SpectatorCard";
import UserCard from "src/components/shared/Cards/UserCard";
import {
  CurrentUserCardContainer,
  TableCardsWrapper,
  TableWrapper,
} from "src/components/Table/Table.components";
import TaskBoard from "src/components/Table/TaskBoard";
import RevealTaskBoard from "src/components/Table/TaskBoard/RevealTaskBoard";
import UserTaskBoard from "src/components/Table/TaskBoard/UserTaskBoard";
import { UserRole } from "src/enums/user/userRoles";
import { useGameContext } from "src/game/useGameContext";
import useIsMobile from "src/hooks/useIsMobile";

const Table: React.FunctionComponent = () => {
  const { gameState } = useGameContext();

  const isMobile = useIsMobile();

  const cards: JSX.Element[] = React.useMemo(() => {
    if (!gameState?.game?.users) {
      return [];
    }
    const newUsers = gameState.game.users.filter(user => user.isConnected);
    const userDestinationIndex = newUsers.length > 4 && !isMobile ? 2 : 0;
    const currentUserIndex =
      newUsers.findIndex(user => user.id === gameState.user?.id) ?? 0;

    // set current user to be in the middle of the bottom part of the table or on top in mobile
    const temp = newUsers[userDestinationIndex];
    newUsers[userDestinationIndex] = newUsers[currentUserIndex];
    newUsers[currentUserIndex] = temp;

    const votes = gameState.voteReveal?.votes
      .map(vote => +vote.value)
      .sort((a, b) => b - a);

    const cards = newUsers.map(user => {
      const value = gameState.voteReveal?.votes.find(
        v => v.user?.id === user.id
      )?.value;
      const isHighest = value && votes && +value === votes[0];
      const isLowest = value && votes && +value === votes[votes.length - 1];

      return user.isSpectator ? (
        <SpectatorCard username={user.name} key={user.id} />
      ) : (
        <UserCard
          username={user.name}
          key={user.id}
          voteReveal={!!gameState.voteReveal}
          picked={
            gameState.game?.task?.votes?.findIndex(
              vote => vote.userId === user.id
            ) !== -1 && !!gameState.game?.task
          }
          value={value}
          highestScore={!!isHighest && isHighest != isLowest}
          lowestScore={!!isLowest && isHighest != isLowest}
        />
      );
    });
    return cards;
  }, [
    gameState?.game?.users,
    gameState.user?.id,
    gameState.voteReveal,
    gameState.game?.task?.votes,
    isMobile,
  ]);

  // split cards by even index
  const [evenCards, oddCards] = cards.reduce<JSX.Element[][]>(
    (a, b, i) => {
      a[i % 2].push(b);
      return a;
    },
    [[], []]
  );

  const renderTaskBoard =
    gameState.user?.role === UserRole.OWNER ? (
      <RevealTaskBoard />
    ) : (
      <UserTaskBoard
        task={gameState.game?.task}
        voteReveal={gameState.voteReveal}
        hasUserPickedCard={
          !!gameState.game?.task?.votes?.find(
            vote => vote.userId === gameState.user?.id
          )
        }
      />
    );

  return (
    <>
      {isMobile && (
        <CurrentUserCardContainer>{cards[0]}</CurrentUserCardContainer>
      )}
      <TableWrapper>
        <TableCardsWrapper numOfCards={cards.length} data-cy="cardsWrapper">
          {oddCards.slice(0, 5)}
        </TableCardsWrapper>
        <TableCardsWrapper data-cy="cardsWrapper" isSideWrapper>
          {oddCards.slice(5)}
        </TableCardsWrapper>
        <TaskBoard gameName={gameState.game?.name}>{renderTaskBoard}</TaskBoard>
        <TableCardsWrapper data-cy="cardsWrapper" isSideWrapper>
          {evenCards.slice(5)}
        </TableCardsWrapper>
        <TableCardsWrapper numOfCards={cards.length} data-cy="cardsWrapper">
          {evenCards.slice(isMobile ? 1 : 0, 5)}
        </TableCardsWrapper>
      </TableWrapper>
    </>
  );
};

export default Table;
